/**
 * <AuditTrailPanel> — collapsible side panel showing the field-level
 * change history for a single record.
 *
 * Usage:
 *   <AuditTrailPanel collection="clients" recordId={cid} />
 *
 * The panel is closed by default (rendered as a button that expands into
 * a slide-over on click) to keep detail pages uncluttered. Data comes from
 * `/workspaces/{ws}/audit/trail?collection=&id=`.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { History, RefreshCw, User } from "lucide-react";

function _stringify(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v || "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

const FIELD_LABEL_OVERRIDES = {
  name: "Name",
  status: "Status",
  health: "Health",
  description: "Description",
  notes: "Notes",
  contact_email: "Contact email",
  contact_phone: "Contact phone",
  planned_start_date: "Planned start",
  planned_end_date: "Planned end",
  actual_start_date: "Actual start",
  actual_end_date: "Actual end",
  estimated_hours: "Estimated hrs",
  actual_hours: "Actual hrs",
  client_id: "Client",
  assignee_id: "Assignee",
  priority: "Priority",
};

function labelFor(field) {
  return FIELD_LABEL_OVERRIDES[field]
    || field.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export default function AuditTrailPanel({ collection, recordId, testid = "audit-trail-panel", buttonClassName = "" }) {
  const { activeWorkspaceId } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId || !collection || !recordId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/audit/trail`, {
        params: { collection, id: recordId, limit: 100 },
      });
      setEntries(data.entries || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, collection, recordId, activeWorkspaceId]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`rounded-none ${buttonClassName}`}
          data-testid={`${testid}-open-btn`}
        >
          <History className="w-3.5 h-3.5 mr-1.5" />
          Audit trail
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto rounded-none" data-testid={testid}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="w-4 h-4" /> Audit trail
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto rounded-none h-7 px-2"
              onClick={load}
              disabled={loading}
              data-testid={`${testid}-refresh`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-2" data-testid={`${testid}-entries`}>
          {loading && entries.length === 0 && (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading changes…</div>
          )}
          {!loading && entries.length === 0 && (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No changes recorded yet. Every future edit will show up here.
            </div>
          )}
          {entries.map((e, i) => (
            <div key={i} className="border border-border p-3 space-y-1.5" data-testid={`${testid}-entry-${i}`}>
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <User className="w-3 h-3 opacity-60" />
                  <span className="font-medium text-foreground">{e.actor_name || "Unknown"}</span>
                  <span>·</span>
                  <span>{fmtRelative(e.at)}</span>
                </span>
                <span className="text-[10px] uppercase tracking-wider border border-border/60 px-1.5 py-0.5">
                  {e.op || "update"}
                </span>
              </div>
              <div className="text-xs">
                <div className="font-medium mb-1">{labelFor(e.field)}</div>
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                  <span className="text-muted-foreground">Old</span>
                  <span className="line-through opacity-60 break-all">{_stringify(e.old)}</span>
                  <span className="text-muted-foreground">New</span>
                  <span className="font-mono text-foreground break-all">{_stringify(e.new)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
